import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, FormControl, Text, useDisclosure, VStack } from "@chakra-ui/react";
import { CryptoTxInput, ExchangeInfo, ExchangeInfoDetail, InputLabel, QuoteDetailsModal, SwapInfo } from "components";
import configs from "config";
import { useCountry, usePartialState, useRedebounce } from "hooks";
import { ICicoQuote, ICicoQuoteRO, IQuote, MapCountry } from "interfaces";
import { createSiweMessage, currencyFormat, isFiat, removeComma, toCoinLocale, toPrecision, when } from "utils";
import { toUpperTrim } from "utils/textFormatter";
import { useLocation } from "react-router";
import { useCicoQuoteInMutation, useCicoSigninMutation, useGetCicoKycStatusQuery, useGetCicoRateQuery } from "apis";
import ls from "utils/secureStorage";
import { getCoinPrecision, MathRound } from "utils/toPrecision";
import { useDispatch, useSelector } from "react-redux";
import { selectForm, setCicoAuthCheck, setSelectedCoinAmount } from "store/slices";
import { BrowserProvider } from "ethers";

interface IProps {
  handleTabChange: (index: number) => void;
  transferType: "in" | "out";
}
// This handles Buy/Sell | in/out
const BuyCicoQuote = ({ handleTabChange, transferType }: IProps) => {
  const { walletAddress } = useSelector(selectForm);

  const location = useLocation();

  const tokens = configs.cicoCoins;

  const { currency } = useCountry();

  const queries = useMemo(() => new URLSearchParams(location?.search), [location]);
  const [state, set] = usePartialState<IQuote>({
    fiatType: when(queries.has("fiat") && isFiat(queries.get("fiat")!), queries.get("fiat")!, currency),
    cryptoType: when(queries.has("coin") && tokens.includes(queries.get("coin")!), queries.get("coin")!, "celo"),
    fiatAmount: when(queries.has("fiatAmount"), queries.get("fiatAmount")!, undefined),
    cryptoAmount: when(queries.has("cryptoAmount"), queries.get("cryptoAmount")!, undefined),
  });

  const redebounce = useRedebounce();
  const { timerExpired } = useSelector(selectForm);

  const [quoteRes, setQuoteRes] = useState<ICicoQuoteRO>();

  const [_cicoQuoteIn] = useCicoQuoteInMutation();

  const sync = useRef(true);
  const { isOpen, onClose, onOpen } = useDisclosure();
  const previewQuote = useRef(true);
  const [isPersistingQuote, setIsPersistingQuote] = useState(false);
  const isPersistingQuoteRef = useRef(false);

  const checkQuoteIn = useCallback(
    async ({ cryptoAmount, cryptoType, fiatAmount, fiatType }: ICicoQuote) => {
      const mapCountry: MapCountry = {
        ngn: "ng",
        ghs: "gh",
        kes: "ke",
      };
      try {
        const isPreview = previewQuote.current;
        if (!isPreview) {
          isPersistingQuoteRef.current = true;
          setIsPersistingQuote(true);
        }

        const response = await _cicoQuoteIn({
          country: mapCountry[fiatType!],
          cryptoType: cryptoType!,
          cryptoAmount: cryptoAmount!,
          fiatType: fiatType!,
          fiatAmount: fiatAmount!,
          // fiatAmount: +(fiatAmount ?? 0)!,
          type: transferType,
        }).unwrap();

        if (response) {
          setQuoteRes(response!);

          set({ cryptoAmount: response.quote.cryptoAmount, fiatAmount: response.quote.fiatAmount });

          const quoteId = response?.quote.quoteId || "";
          ls.set(configs.CICO_QUOTE_ID, quoteId);
        }

        if (!state.address) {
          set({ address: walletAddress });
        }

        isPersistingQuoteRef.current = false;
        setIsPersistingQuote(false);
      } catch (error) {
        setIsPersistingQuote(false);
        isPersistingQuoteRef.current = false;
        sync.current = false;
      }
    },

    [_cicoQuoteIn, set, state.address, transferType, walletAddress]
  );

  const handleFiat = async (value: string) => {
    set({ fiatType: value as any });
    if (+(state?.fiatAmount ?? 0) <= 0) return;

    const { cryptoAmount, cryptoType, fiatAmount } = state;

    redebounce(
      () =>
        checkQuoteIn({
          cryptoAmount: cryptoAmount!,
          cryptoType: cryptoType!,
          fiatAmount: fiatAmount!,
          fiatType: value!,
          type: transferType,
        }),
      "quoteIn",
      1000
    )();
  };

  const handleFiatAmount = useCallback(
    (value: string) => {
      const newValue = removeComma(value);

      set({ fiatAmount: newValue });
      if (+(newValue ?? 0) <= 0) return;

      const { cryptoType, fiatType } = state;
      redebounce(
        () =>
          checkQuoteIn({
            cryptoAmount: undefined!,
            cryptoType: cryptoType!,
            fiatAmount: newValue,
            fiatType: fiatType!,
            type: transferType,
          }),
        "quoteIn",
        500
      )();
    },
    [checkQuoteIn, redebounce, set, state, transferType]
  );

  const handleCrypto = (coin: string) => {
    set({ cryptoType: coin as any });
    if (+(state?.fiatAmount ?? 0) <= 0) return;

    const { cryptoAmount, fiatAmount, fiatType } = state;
    redebounce(
      () =>
        checkQuoteIn({
          cryptoAmount: cryptoAmount!,
          cryptoType: coin!,
          fiatAmount: fiatAmount!,
          fiatType: fiatType!,
          type: transferType,
        }),
      "quoteIn",
      1000
    )();
  };

  const handleCryptoAmount = (value: string) => {
    const updatedValue = removeComma(value);
    set({ cryptoAmount: updatedValue });

    if (+(updatedValue ?? 0) <= 0) return;

    const { cryptoType, fiatType } = state;

    console.log("HERE");

    redebounce(
      () =>
        checkQuoteIn({
          cryptoAmount: updatedValue,
          cryptoType: cryptoType!,
          fiatAmount: undefined!,
          fiatType: fiatType!,
          type: transferType,
        }),
      "quoteIn",
      1000
    )();
  };

  const refetchQuote = useCallback(
    async (value: string) => {
      const newValue = removeComma(value);
      set({ fiatAmount: newValue });
      if (+(newValue ?? 0) <= 0) return;

      await checkQuoteIn({
        cryptoAmount: undefined!,
        cryptoType: state.cryptoType!,
        fiatAmount: state.fiatAmount!,
        fiatType: state.fiatType!,
        type: transferType,
      });
    },
    [checkQuoteIn, set, state.cryptoType, state.fiatAmount, state.fiatType, transferType]
  );

  const lastPersisted = useRef(false);

  const persistQuote = useCallback(
    async (e?: any) => {
      if (e) e.preventDefault();

      if (lastPersisted.current) {
        // will not make new request, but will let the function to proceed with existing quote
        return true;
      }

      if ((state.fiatAmount || state.cryptoAmount) && state.cryptoType && state.fiatType) {
        previewQuote.current = false;
        await refetchQuote(String(state.fiatAmount));
      }
      return true;
    },
    [refetchQuote, state]
  );

  const formattedCryptoType = useMemo(() => {
    return toCoinLocale(state?.cryptoType! || "");
  }, [state.cryptoType]);

  const amountGotten = useMemo(() => {
    const cryptoAmount = quoteRes?.quote?.cryptoAmount ?? 0;
    const cryptoAmountMinusFees = Number(cryptoAmount) - Number(quoteRes?.quote?.fee ?? 0);
    const roundedAmount = MathRound(cryptoAmountMinusFees, getCoinPrecision(state?.cryptoType || "", 12));
    return `You get ${Number(roundedAmount).toLocaleString()} ${formattedCryptoType} for ${currencyFormat(
      (state?.fiatType as any) ?? currency
    ).format(+(quoteRes?.quote?.fiatAmount ?? 0))}`;
  }, [quoteRes, state, formattedCryptoType, currency]);

  const { data: rateData, isLoading: isRateLoading } = useGetCicoRateQuery({
    to: transferType === "in" ? state.fiatType! : state.cryptoType!,
    from: transferType === "in" ? state.cryptoType! : state.fiatType!,
  });

  const isDisabled = useMemo(() => {
    return (
      !(state?.fiatAmount && state?.fiatType && state?.cryptoAmount && state?.cryptoAmount) ||
      isRateLoading ||
      !quoteRes ||
      timerExpired
    );
  }, [isRateLoading, quoteRes, state?.cryptoAmount, state?.fiatAmount, state?.fiatType, timerExpired]);

  const [_cicoAuth] = useCicoSigninMutation();

  const dispatch = useDispatch();

  const signInWithEthereum = async () => {
    const provider = new BrowserProvider(window.ethereum);

    const signer = await provider?.getSigner();
    let address = signer.address;

    const message = createSiweMessage({
      address: address,
      statement: address,
    });

    const signature = await signer?.signMessage(message);

    const res: any = await _cicoAuth({
      message,
      signature,
      address,
    });

    if (res.data) {
      ls.set(configs.CICO_TOKEN_KEY, res.data.token);
      dispatch(setCicoAuthCheck(true));

      window.location.replace(`?amount=${state.fiatAmount}&fiat=${state.fiatType}&coin=${state.cryptoType}`);
    }
  };

  const cicoAuth = ls.get(configs.CICO_TOKEN_KEY) as string;
  const { data: cicoStatus } = useGetCicoKycStatusQuery(null, {
    skip: cicoAuth ? false : true,
  });

  const handleContinue = () => {
    if (quoteRes?.kyc.kycRequired && cicoStatus?.status !== "KycApproved") {
      handleTabChange(3);
    } else {
      handleTabChange(1);
    }
    onClose();
  };

  const cicoToken = useMemo(() => ls.get(configs.CICO_TOKEN_KEY), []);

  useEffect(() => {
    const amount = queries.get("amount");
    amount && handleFiatAmount(amount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  return (
    <>
      <form
        onSubmit={async (e) => {
          e?.preventDefault();
          dispatch(setSelectedCoinAmount(state.cryptoAmount));
          if (!cicoToken) {
            await signInWithEthereum();
          }

          await persistQuote(e);
          onOpen();
        }}
      >
        {transferType === "in" && (
          <>
            <FormControl mb="20px !important">
              <InputLabel htmlFor="amount" isLoading={false}>
                Amount in {toUpperTrim(state.fiatType || "Fiat")}
              </InputLabel>

              <CryptoTxInput
                name="amount"
                id="amount"
                placeholder="Enter Amount"
                type="text"
                coinValue={state?.fiatType ?? ""}
                value={state.fiatAmount ? Number(state.fiatAmount).toLocaleString() : ""}
                onChange={(e) => handleFiatAmount(e.target.value)}
                pattern="^\d+(,\d+)*$"
                onCoinChange={handleFiat}
                _select={{ borderColor: "transparent" }}
                walletType={configs.fiats}
                selectTitle="Which currency do you want to pay with?"
              >
                {(configs.fiats ?? [])?.map((fiat) => (
                  <option key={`buy-crypto-${fiat}`} value={fiat}>
                    {toCoinLocale(fiat)}
                  </option>
                ))}
              </CryptoTxInput>
            </FormControl>

            <FormControl mb="20px !important">
              <InputLabel htmlFor="volume">Volume in {toUpperTrim(state.cryptoType || "Token")}</InputLabel>
              <CryptoTxInput
                id="volume"
                placeholder="Equivalent Volume"
                type="text"
                value={state.cryptoAmount ? Number(state.cryptoAmount).toLocaleString() : ""}
                coinValue={state?.cryptoType ?? ""}
                network={state?.network ?? ""}
                onChange={(e) => handleCryptoAmount(e.target.value)}
                onCoinChange={handleCrypto}
                _select={{ borderColor: "transparent" }}
                walletType={tokens}
                selectTitle="Which crypto do you want to buy?"
              >
                {(tokens ?? [])?.map((token) => (
                  <option key={`buy-crypto-${token}`} value={token}>
                    {toCoinLocale(token)}
                  </option>
                ))}
              </CryptoTxInput>
            </FormControl>
          </>
        )}

        {transferType === "out" && (
          <>
            <FormControl mb="20px !important">
              <InputLabel htmlFor="volume">Volume in {toUpperTrim(state.cryptoType || "Token")}</InputLabel>
              <CryptoTxInput
                id="volume"
                placeholder="Equivalent Volume"
                type="text"
                value={state.cryptoAmount ? Number(state.cryptoAmount).toLocaleString() : ""}
                coinValue={state?.cryptoType ?? ""}
                network={state?.network ?? ""}
                onChange={(e) => handleCryptoAmount(e.target.value)}
                onCoinChange={handleCrypto}
                _select={{ borderColor: "transparent" }}
                walletType={tokens}
                selectTitle="Which crypto do you want to buy?"
              >
                {(tokens ?? [])?.map((token) => (
                  <option key={`buy-crypto-${token}`} value={token}>
                    {toCoinLocale(token)}
                  </option>
                ))}
              </CryptoTxInput>
            </FormControl>

            <FormControl mb="20px !important">
              <InputLabel htmlFor="amount" isLoading={false}>
                Amount in {toUpperTrim(state.fiatType || "Fiat")}
              </InputLabel>

              <CryptoTxInput
                name="amount"
                id="amount"
                placeholder="Enter Amount"
                type="text"
                coinValue={state?.fiatType ?? ""}
                value={state.fiatAmount ? Number(state.fiatAmount).toLocaleString() : ""}
                onChange={(e) => handleFiatAmount(e.target.value)}
                pattern="^\d+(,\d+)*$"
                onCoinChange={handleFiat}
                _select={{ borderColor: "transparent" }}
                walletType={configs.fiats}
                selectTitle="Which currency do you want to pay with?"
              >
                {(configs.fiats ?? [])?.map((fiat) => (
                  <option key={`buy-crypto-${fiat}`} value={fiat}>
                    {toCoinLocale(fiat)}
                  </option>
                ))}
              </CryptoTxInput>
            </FormControl>
          </>
        )}

        <SwapInfo isLoading={isRateLoading}>
          1 {formattedCryptoType} ={" "}
          {rateData
            ? currencyFormat((state?.fiatType as any) ?? currency).format(
                transferType === "in" ? rateData?.buy! : rateData?.sell! ?? 0
              )
            : ""}
        </SwapInfo>

        {!!quoteRes && (
          <>
            <ExchangeInfo mt="24px !important" title={amountGotten}>
              <ExchangeInfoDetail
                title={`${toPrecision(+(state?.cryptoAmount ?? 0), state?.cryptoType ?? "celo")} @ ${currencyFormat(
                  (state?.fiatType as any) ?? currency
                ).format(Number(rateData?.buy!) ?? 0)}`}
                description={currencyFormat((state?.fiatType as any) ?? currency).format(+(state?.fiatAmount ?? 0))}
              />

              <ExchangeInfoDetail
                title={quoteRes.quote.feeType}
                description={`${quoteRes?.quote?.fee || 0} `.concat(formattedCryptoType)}
              />
            </ExchangeInfo>
          </>
        )}

        <Button
          w="100%"
          minW={{ base: "auto", "2sm": "400px" }}
          type="submit"
          mt="42px"
          isDisabled={isDisabled || isPersistingQuote}
        >
          Continue
        </Button>
      </form>

      {quoteRes && (
        <QuoteDetailsModal
          isOpen={isOpen}
          onClose={onClose}
          time={new Date(quoteRes?.quote?.guaranteedUntil!).toISOString()}
          onExpired={async () => {
            if (!!state?.address && +(state?.fiatAmount ?? 0) > 0 && !!sync.current) {
              await persistQuote();
            }
          }}
          // TODO: Get the minutes count from guaranteedUntil
          minuteCount={4}
        >
          <Box borderRadius="10px" bg="#F9FAF9" pb={{ base: "17px", md: "21px" }}>
            <ExchangeInfo mt="24px !important" title={amountGotten}>
              <ExchangeInfoDetail
                title={`${toPrecision(+(state?.cryptoAmount ?? 0), state?.cryptoType ?? "celo")} @ ${currencyFormat(
                  (state?.fiatType as any) ?? currency
                ).format(Number(rateData?.buy) ?? 0)}`}
                description={currencyFormat((state?.fiatType as any) ?? currency).format(+(state?.fiatAmount ?? 0))}
              />
              <ExchangeInfoDetail title="Fee" description={`${quoteRes?.quote?.fee || 0} `.concat(formattedCryptoType)} />
            </ExchangeInfo>

            <Text px="12px" mt="10px" fontSize={{ base: "12px", md: "13px" }} color="#ED1C00">
              Kindly note that purchased coins will arrive within 45mins and is subject to delays from banking institutions
            </Text>
          </Box>

          <Box margin={{ base: "53px 0 24px", md: "53px 0 0" }}>
            <VStack mt="34px !important">
              <Button
                w="100%"
                minW={{ base: "auto", "2sm": "400px" }}
                isDisabled={isDisabled || isPersistingQuote}
                isLoading={isPersistingQuote || isPersistingQuoteRef.current}
                onClick={handleContinue}
              >
                Proceed with trade
              </Button>
            </VStack>
          </Box>

          <Box color="#0D4740" textDecor="underline" display={{ base: "block", md: "none" }} textAlign="center" onClick={onClose}>
            Cancel
          </Box>
        </QuoteDetailsModal>
      )}
    </>
  );
};

export default BuyCicoQuote;
